/**
 * 敏感词校验工具
 * 使用 DFA (Deterministic Finite Automaton) 算法实现高效的敏感词检测
 */

/**
 * 敏感词DFA字典树节点类型
 */
type WordMapNode = Map<string, WordMapNode> & {
  isEnd?: boolean;
};

/**
 * 敏感词检测器类
 */
class SensitiveWordsDetector {
  private wordMap: WordMapNode | null = null;
  private wordList: string[] = [];
  private minMatchLength: number = 2; // 最小匹配长度，默认2（忽略单字）
  private useWordBoundary: boolean = true; // 是否使用单词边界检测，默认true

  /**
   * 判断字符是否为单词字符（字母、数字、中文等）
   * @param char 字符
   * @returns 是否为单词字符
   */
  private isWordChar(char: string): boolean {
    if (!char) return false;
    const code = char.charCodeAt(0);
    return (
      (code >= 48 && code <= 57) || // 0-9
      (code >= 65 && code <= 90) || // A-Z
      (code >= 97 && code <= 122) || // a-z
      (code >= 0x4e00 && code <= 0x9fff) || // 中文字符
      (code >= 0x3400 && code <= 0x4dbf) || // 中文扩展A
      (code >= 0x20000 && code <= 0x2a6df) || // 中文扩展B
      char === '_' || char === '-' // 允许下划线和连字符作为单词的一部分
    );
  }

  /**
   * 判断字符是否为中文字符
   * @param char 字符
   * @returns 是否为中文字符
   */
  private isChineseChar(char: string): boolean {
    if (!char) return false;
    const code = char.charCodeAt(0);
    return (
      (code >= 0x4e00 && code <= 0x9fff) || // 中文字符
      (code >= 0x3400 && code <= 0x4dbf) || // 中文扩展A
      (code >= 0x20000 && code <= 0x2a6df)  // 中文扩展B
    );
  }

  /**
   * 判断字符串是否主要包含中文字符
   * @param text 文本
   * @returns 是否主要是中文
   */
  private isPrimarilyChinese(text: string): boolean {
    if (!text) return false;
    let chineseCount = 0;
    for (const char of text) {
      if (this.isChineseChar(char)) {
        chineseCount++;
      }
    }
    // 如果超过50%是中文字符，认为是中文文本
    return chineseCount > text.length * 0.5;
  }

  /**
   * 检查位置是否是单词边界（仅对英文等有空格的语言有效）
   * @param text 完整文本
   * @param position 要检查的位置
   * @returns 是否是单词边界（位置为0、末尾、或前后字符不是单词字符）
   */
  private isWordBoundary(text: string, position: number): boolean {
    if (position < 0 || position > text.length) return true;
    if (position === 0 || position === text.length) return true;
    
    const prevChar = position > 0 ? text[position - 1] : '';
    const currChar = position < text.length ? text[position] : '';
    
    // 如果前一个字符不是单词字符，或当前字符不是单词字符，则是边界
    return !this.isWordChar(prevChar) || !this.isWordChar(currChar);
  }

  /**
   * 将字符标准化（非中文字符转小写）
   * @param char 字符
   * @returns 标准化后的字符
   */
  private normalizeChar(char: string): string {
    // 中文字符不转换，保持原样
    if (this.isChineseChar(char)) {
      return char;
    }
    // 非中文字符转小写
    return char.toLowerCase();
  }

  /**
   * 构建敏感词 DFA 字典树
   * @param wordList 敏感词列表
   * @returns 构建好的DFA字典树根节点
   */
  private buildWordMap(wordList: string[]): WordMapNode {
    const root: WordMapNode = new Map() as WordMapNode;

    wordList.forEach(word => {
      if (!word || word.trim().length === 0) return;

      let node = root;
      const trimmedWord = word.trim();

      // 构建字典树，包含所有字符（英文转小写，中文保持原样）
      for (const char of trimmedWord) {
        const normalizedChar = this.normalizeChar(char);
        if (!node.has(normalizedChar)) {
          node.set(normalizedChar, new Map() as WordMapNode);
        }
        node = node.get(normalizedChar)!;
      }

      // 标记词语的结束
      node.isEnd = true;
    });

    return root;
  }

  /**
   * 初始化敏感词列表
   * @param wordList 敏感词列表
   * @param minMatchLength 最小匹配长度，默认2（忽略单字匹配）。设置为1则检测所有长度
   * @param useWordBoundary 是否使用单词边界检测，默认true（推荐）
   */
  public initialize(wordList: string[], minMatchLength: number = 2, useWordBoundary: boolean = true): void {
    if (!wordList || wordList.length === 0) {
      this.wordMap = null;
      this.wordList = [];
      return;
    }

    this.minMatchLength = Math.max(1, minMatchLength); // 至少为1
    this.useWordBoundary = useWordBoundary;
    this.wordList = [...wordList];
    this.wordMap = this.buildWordMap(wordList);
  }

  /**
   * 使用 DFA 进行敏感词查找（连续字符串匹配）
   * @param text 待检查文本
   * @param maxResults 最多返回的敏感词数量，默认3
   * @returns 找到的敏感词列表（去重，最多返回maxResults个）
   */
  public findSensitiveWords(text: string, maxResults: number = 3): string[] {
    if (!text || text.trim().length === 0) {
      return [];
    }

    if (!this.wordMap) {
      return [];
    }

    const foundWords = new Set<string>();
    const len = text.length;

    // 从每个字符位置开始查找
    for (let i = 0; i < len && foundWords.size < maxResults; i++) {
      let node: WordMapNode | undefined = this.wordMap;
      let matchWord = '';

      // 从当前字符开始查找（连续匹配）
      for (let j = i; j < len && node; j++) {
        const char = text[j];
        const normalizedChar = this.normalizeChar(char);

        // 尝试匹配当前字符（标准化后）
        node = node.get(normalizedChar);

        if (node) {
          matchWord += char;

          // 发现完整词
          if (node.isEnd) {
            // 检查匹配长度是否满足最小长度要求
            if (matchWord.length >= this.minMatchLength) {
              let shouldAdd = true;

              // 如果启用了单词边界检测，需要根据敏感词的语言类型决定是否检查边界
              if (this.useWordBoundary) {
                const isChinese = this.isPrimarilyChinese(matchWord);
                
                if (!isChinese) {
                  // 非中文敏感词（英文、西班牙语等）：需要检查单词边界
                  const startPos = i;
                  const endPos = j + 1;
                  shouldAdd = this.isWordBoundary(text, startPos) && this.isWordBoundary(text, endPos);
                }
                // 中文敏感词：不检查边界，直接添加
              }

              if (shouldAdd) {
                foundWords.add(matchWord);
              }
            }
            // 继续匹配更长的词（例如 "porn" -> "porn site"）
          }

          // 如果已经找到足够的结果，可以提前退出
          if (foundWords.size >= maxResults) {
            break;
          }
        } else {
          // 状态转移失败，跳出当前匹配路径
          break;
        }
      }
    }

    return Array.from(foundWords).slice(0, maxResults);
  }

  /**
   * 检查文本是否包含敏感词
   * @param text 待检查文本
   * @returns 是否包含敏感词
   */
  public hasSensitiveWords(text: string): boolean {
    const words = this.findSensitiveWords(text, 1);
    return words.length > 0;
  }

  /**
   * 获取当前敏感词列表
   * @returns 敏感词列表的副本
   */
  public getWordList(): string[] {
    return [...this.wordList];
  }

  /**
   * 检查是否已初始化
   * @returns 是否已初始化
   */
  public isInitialized(): boolean {
    return this.wordMap !== null;
  }
}

// 创建全局单例实例
const detector = new SensitiveWordsDetector();

/**
 * 初始化敏感词列表
 * @param wordList 敏感词列表
 * @param minMatchLength 最小匹配长度，默认2（忽略单字匹配）。设置为1则检测所有长度
 * @param useWordBoundary 是否使用单词边界检测，默认true（推荐）
 */
export const initializeSensitiveWords = (wordList: string[], minMatchLength: number = 2, useWordBoundary: boolean = true): void => {
  detector.initialize(wordList, minMatchLength, useWordBoundary);
};

/**
 * 查找文本中的敏感词
 * @param text 待检查文本
 * @param maxResults 最多返回的敏感词数量，默认3
 * @returns 找到的敏感词列表
 */
export const findSensitiveWords = (text: string, maxResults: number = 3): string[] => {
  return detector.findSensitiveWords(text, maxResults);
};

/**
 * 检查文本是否包含敏感词
 * @param text 待检查文本
 * @returns 是否包含敏感词
 */
export const hasSensitiveWords = (text: string): boolean => {
  return detector.hasSensitiveWords(text);
};

/**
 * 获取当前敏感词列表
 * @returns 敏感词列表的副本
 */
export const getSensitiveWordList = (): string[] => {
  return detector.getWordList();
};

/**
 * 检查敏感词列表是否已初始化
 * @returns 是否已初始化
 */
export const isSensitiveWordsInitialized = (): boolean => {
  return detector.isInitialized();
};

